import { Injectable } from '@angular/core';
import { RSA_PUBLIC_KEY } from 'src/environments/kays-cryptage/public-key';

@Injectable({ providedIn: 'root' })
export class EncryptionService {

  private cachedKey: CryptoKey | null = null;

  async getPublicKey(): Promise<CryptoKey> {
    if (this.cachedKey) return this.cachedKey;
    this.cachedKey = await this.importPublicKey(RSA_PUBLIC_KEY);
    return this.cachedKey;
  }

  // Chiffre un objet JSON — retourne { encryptedKey, iv, ciphertext, authTag } en base64
  async encryptBody(body: object): Promise<object> {
    const publicKey = await this.getPublicKey();

    const aesKey = await crypto.subtle.generateKey(
      { name: 'AES-GCM', length: 256 },
      true,
      ['encrypt']
    );
    const iv = crypto.getRandomValues(new Uint8Array(12));

    const encoded = new TextEncoder().encode(JSON.stringify(body));
    const encrypted = await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv, tagLength: 128 },
      aesKey,
      encoded
    );

    // Web Crypto AES-GCM : les 16 derniers bytes sont le authTag
    const encryptedBytes = new Uint8Array(encrypted);
    const ciphertext = encryptedBytes.slice(0, encryptedBytes.length - 16);
    const authTag    = encryptedBytes.slice(encryptedBytes.length - 16);

    const rawAesKey    = await crypto.subtle.exportKey('raw', aesKey);
    const encryptedKey = await crypto.subtle.encrypt(
      { name: 'RSA-OAEP' },
      publicKey,
      rawAesKey
    );

    return {
      encryptedKey: this.toBase64(encryptedKey),
      iv:           this.toBase64(iv),
      ciphertext:   this.toBase64(ciphertext),
      authTag:      this.toBase64(authTag),
    };
  }

  private async importPublicKey(pem: string): Promise<CryptoKey> {
    const b64 = pem
      .replace('-----BEGIN PUBLIC KEY-----', '')
      .replace('-----END PUBLIC KEY-----', '')
      .replace(/\n/g, '');
    const der = Uint8Array.from(atob(b64), c => c.charCodeAt(0));
    return crypto.subtle.importKey(
      'spki',
      der,
      { name: 'RSA-OAEP', hash: 'SHA-256' },
      false,
      ['encrypt']
    );
  }

  private toBase64(buffer: ArrayBuffer | Uint8Array): string {
    const bytes = buffer instanceof Uint8Array ? buffer : new Uint8Array(buffer);
    let binary = '';
    for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i]);
    return btoa(binary);
  }
}
